import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router';
import { MapSidebarContainer } from 'components/MapPage/MapSidebarComponents';
import { SitStatWorkspace } from 'components/MapPage/CorporateViewTabs/SitStatTab/SitStatWorkspace';
import {
  SITSTAT_AOR_PARAM,
  SITSTAT_OPEN_TAB,
  SITSTAT_TEMPLATE_PARAM,
} from 'components/MapPage/CorporateViewTabs/SitStatTab/sitstat-navigation';
import { SITSTAT_AOR_VIEW_TEMPLATE_ID } from 'components/MapPage/CorporateViewTabs/SitStatTab/sitstat-system-templates';
import { registerOrgWorkspaceTourApi } from './orgWorkspaceTour/orgWorkspaceTourBridge';
import { registerFocCopLayersTourApi } from './focCopLayersTour/focCopLayersTourBridge';
import { useUscgMapContext } from './CorporateViewTabs/LayersTab/UscgMapContext';
import { UscgMapContainer } from './UscgMapContainer';

const EXIT_SITSTAT_OPEN_TAB = 'Layers';

type UscgMapLayoutProps = {
  children: ReactNode;
};

export function UscgMapLayout({ children }: UscgMapLayoutProps) {
  const [isMainSidebarVisible, setIsMainSidebarVisible] = useState(true);
  const { isMapVisible, setIsMapVisible } = useUscgMapContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const openTab = useMemo(
    () => searchParams.get('openTab') || 'Notifications',
    [searchParams]
  );
  const isSitStatMode = openTab === SITSTAT_OPEN_TAB;

  useEffect(() => {
    if (isSitStatMode && !isMapVisible) {
      setIsMapVisible(true);
    }
  }, [isMapVisible, isSitStatMode, setIsMapVisible]);

  const exitSitStat = useCallback(() => {
    setSearchParams(
      (params) => {
        params.set('openTab', EXIT_SITSTAT_OPEN_TAB);
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useEffect(
    () =>
      registerOrgWorkspaceTourApi({
        isSitStatOpen: () => isSitStatMode,
        openAorViewSitStat: (aorId) => {
          setSearchParams((params) => {
            const next = new URLSearchParams(params);
            next.set('openTab', SITSTAT_OPEN_TAB);
            next.set(SITSTAT_TEMPLATE_PARAM, SITSTAT_AOR_VIEW_TEMPLATE_ID);
            if (aorId) next.set(SITSTAT_AOR_PARAM, aorId);
            else next.delete(SITSTAT_AOR_PARAM);
            return next;
          });
        },
        exitSitStatToAors: () => {
          setSearchParams(
            (params) => {
              const next = new URLSearchParams(params);
              next.set('openTab', 'AORs');
              return next;
            },
            { replace: true }
          );
        },
      }),
    [isSitStatMode, setSearchParams]
  );

  useEffect(
    () =>
      registerFocCopLayersTourApi({
        getOpenTab: () => openTab,
        showLayers: () => {
          setSearchParams(
            (params) => {
              const next = new URLSearchParams(params);
              next.set('openTab', 'Layers');
              return next;
            },
            { replace: true }
          );
        },
        openAors: () => {
          setSearchParams(
            (params) => {
              const next = new URLSearchParams(params);
              next.set('openTab', 'AORs');
              return next;
            },
            { replace: true }
          );
        },
        isSitStatOpen: () => isSitStatMode,
        openSitStatViews: () => {
          setSearchParams((params) => {
            const next = new URLSearchParams(params);
            next.set('openTab', SITSTAT_OPEN_TAB);
            next.set(SITSTAT_TEMPLATE_PARAM, SITSTAT_AOR_VIEW_TEMPLATE_ID);
            return next;
          });
        },
        exitSitStatToAors: () => {
          setSearchParams(
            (params) => {
              const next = new URLSearchParams(params);
              next.set('openTab', 'AORs');
              return next;
            },
            { replace: true }
          );
        },
      }),
    [isSitStatMode, openTab, setSearchParams]
  );

  if (isSitStatMode) {
    return (
      <div className="d-flex h-100 w-100" style={{ overflow: 'hidden' }}>
        <div className="h-100 w-100" style={{ minHeight: 0 }}>
          <SitStatWorkspace
            mapSlot={<UscgMapContainer />}
            onExitSitStat={exitSitStat}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        isMapVisible
          ? 'd-flex h-100 w-100'
          : 'dark d-flex tw:text-foreground h-100 w-100'
      }
      style={{ overflow: 'hidden', minHeight: 0 }}
    >
      <MapSidebarContainer
        isMainSidebarVisible={isMapVisible ? isMainSidebarVisible : true}
        setIsMainSidebarVisible={setIsMainSidebarVisible}
        fullWidth={!isMapVisible}
      >
        {children}
      </MapSidebarContainer>
      {isMapVisible ? <UscgMapContainer /> : null}
    </div>
  );
}
